import React, { useState, useEffect } from 'react';

interface AdaptiveLearningPreviewProps {
  onClick?: () => void;
}

type DifficultyLevel = 'easy' | 'medium' | 'hard' | 'challenge';

interface Question {
  difficulty: DifficultyLevel;
  question: string;
  correct: boolean | null;
}

const AdaptiveLearningPreview: React.FC<AdaptiveLearningPreviewProps> = ({ onClick }) => {
  const [currentDifficulty, setCurrentDifficulty] = useState<DifficultyLevel>('medium');
  const [streak, setStreak] = useState(0);
  const [questionHistory, setQuestionHistory] = useState<Question[]>([]);
  const [isAnswering, setIsAnswering] = useState(false);
  const [showAdjustment, setShowAdjustment] = useState(false);

  const difficultyLevels: { level: DifficultyLevel; label: string; color: string }[] = [
    { level: 'easy', label: 'Easy', color: '#10b981' },
    { level: 'medium', label: 'Medium', color: '#3b82f6' },
    { level: 'hard', label: 'Hard', color: '#f59e0b' },
    { level: 'challenge', label: 'Challenge', color: '#ef4444' },
  ];

  // Simulate adaptive question flow
  useEffect(() => {
    const simulateQuestion = () => {
      setIsAnswering(true);

      // Simulate answering
      setTimeout(() => {
        const isCorrect = Math.random() > 0.3; // 70% success rate
        const newQuestion: Question = {
          difficulty: currentDifficulty,
          question: `Q${questionHistory.length + 1}`,
          correct: isCorrect,
        };

        setQuestionHistory(prev => [...prev.slice(-5), newQuestion]);
        setIsAnswering(false);

        if (isCorrect) {
          setStreak(prev => prev + 1);
          // Increase difficulty after 2 correct
          if (streak >= 1) {
            const currentIdx = difficultyLevels.findIndex(d => d.level === currentDifficulty);
            if (currentIdx < difficultyLevels.length - 1) {
              setShowAdjustment(true);
              setTimeout(() => {
                setCurrentDifficulty(difficultyLevels[currentIdx + 1].level);
                setShowAdjustment(false);
                setStreak(0);
              }, 1000);
            }
          }
        } else {
          setStreak(0);
          // Decrease difficulty after wrong answer
          const currentIdx = difficultyLevels.findIndex(d => d.level === currentDifficulty);
          if (currentIdx > 0) {
            setShowAdjustment(true);
            setTimeout(() => {
              setCurrentDifficulty(difficultyLevels[currentIdx - 1].level);
              setShowAdjustment(false);
            }, 1000);
          }
        }
      }, 1500);
    };

    const interval = setInterval(simulateQuestion, 3500);
    simulateQuestion(); // Start immediately

    return () => clearInterval(interval);
  }, [currentDifficulty, streak, questionHistory.length]);

  const getCurrentColor = () => {
    return difficultyLevels.find(d => d.level === currentDifficulty)?.color || '#3b82f6';
  };

  const getDifficultyIndex = () => {
    return difficultyLevels.findIndex(d => d.level === currentDifficulty);
  };

  return (
    <div
      className="relative cursor-pointer transition-all duration-300 hover:scale-[1.02] group"
      onClick={onClick}
      style={{
        background: 'linear-gradient(180deg, rgba(30, 41, 59, 0.95) 0%, rgba(15, 23, 42, 0.98) 100%)',
        borderRadius: '12px',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        overflow: 'hidden',
        width: '320px',
      }}
    >
      {/* Header */}
      <div
        className="px-4 py-2 border-b flex items-center justify-between"
        style={{
          borderColor: 'rgba(255, 255, 255, 0.1)',
          background: 'rgba(139, 92, 246, 0.05)',
        }}
      >
        <span className="text-xs font-semibold" style={{ color: '#94a3b8' }}>
          AI Adaptive Engine
        </span>
        <div className="flex items-center gap-1">
          <svg className="w-3 h-3" style={{ color: '#8b5cf6' }} viewBox="0 0 24 24" fill="currentColor">
            <path d="M13 10V3L4 14h7v7l9-11h-7z" />
          </svg>
          <span className="text-[10px]" style={{ color: '#8b5cf6' }}>
            AI-Powered
          </span>
        </div>
      </div>

      {/* Difficulty Meter */}
      <div className="p-4">
        <div className="text-[10px] font-semibold mb-2" style={{ color: '#64748b' }}>
          CURRENT DIFFICULTY
        </div>
        <div className="flex items-center gap-2 mb-4">
          {difficultyLevels.map((level, idx) => (
            <div key={level.level} className="flex-1">
              <div
                className="h-2 rounded-full transition-all duration-500"
                style={{
                  background: idx <= getDifficultyIndex() ? level.color : 'rgba(255, 255, 255, 0.1)',
                  transform: idx === getDifficultyIndex() ? 'scaleY(1.5)' : 'scaleY(1)',
                }}
              />
              <div
                className="text-[8px] text-center mt-1 transition-all duration-300"
                style={{
                  color: idx === getDifficultyIndex() ? level.color : '#475569',
                  fontWeight: idx === getDifficultyIndex() ? 600 : 400,
                }}
              >
                {level.label}
              </div>
            </div>
          ))}
        </div>

        {/* Current Question Simulation */}
        <div
          className="p-3 rounded-lg mb-3 transition-all duration-300"
          style={{
            background: `${getCurrentColor()}15`,
            border: `1px solid ${getCurrentColor()}30`,
          }}
        >
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-medium" style={{ color: getCurrentColor() }}>
              {difficultyLevels.find(d => d.level === currentDifficulty)?.label} Question
            </span>
            {isAnswering && (
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: getCurrentColor(), borderTopColor: 'transparent' }} />
                <span className="text-[10px]" style={{ color: '#64748b' }}>
                  Answering...
                </span>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center"
              style={{ background: `${getCurrentColor()}30` }}
            >
              <span className="text-lg">🧮</span>
            </div>
            <div className="flex-1">
              <div className="text-xs" style={{ color: '#e2e8f0' }}>
                Solve: 7 × {currentDifficulty === 'easy' ? '3' : currentDifficulty === 'medium' ? '8' : currentDifficulty === 'hard' ? '12' : '15'} = ?
              </div>
            </div>
          </div>
        </div>

        {/* Adjustment Alert - fixed height to prevent layout shift */}
        <div
          className="h-9 mb-3"
        >
          <div
            className={`p-2 rounded-lg flex items-center gap-2 transition-all duration-300 ${showAdjustment ? 'opacity-100' : 'opacity-0'}`}
            style={{
              background: 'rgba(139, 92, 246, 0.2)',
              border: '1px solid rgba(139, 92, 246, 0.3)',
            }}
          >
            <svg className={`w-4 h-4 ${showAdjustment ? 'animate-pulse' : ''}`} style={{ color: '#8b5cf6' }} viewBox="0 0 24 24" fill="currentColor">
              <path d="M13 10V3L4 14h7v7l9-11h-7z" />
            </svg>
            <span className="text-xs" style={{ color: '#8b5cf6' }}>
              AI adjusting difficulty...
            </span>
          </div>
        </div>

        {/* Question History */}
        <div className="text-[10px] font-semibold mb-2" style={{ color: '#64748b' }}>
          RECENT RESPONSES
        </div>
        <div className="flex gap-1">
          {[...Array(6)].map((_, idx) => {
            const question = questionHistory[idx];
            return (
              <div
                key={idx}
                className="flex-1 h-8 rounded flex items-center justify-center transition-all duration-300"
                style={{
                  background: question
                    ? question.correct
                      ? 'rgba(16, 185, 129, 0.2)'
                      : 'rgba(239, 68, 68, 0.2)'
                    : 'rgba(255, 255, 255, 0.05)',
                  border: question
                    ? question.correct
                      ? '1px solid rgba(16, 185, 129, 0.3)'
                      : '1px solid rgba(239, 68, 68, 0.3)'
                    : '1px solid transparent',
                }}
              >
                {question && (
                  <span
                    className="text-sm"
                    style={{ color: question.correct ? '#10b981' : '#ef4444' }}
                  >
                    {question.correct ? '✓' : '✗'}
                  </span>
                )}
              </div>
            );
          })}
        </div>

        {/* Streak Counter */}
        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="text-lg">🔥</span>
            <span className="text-xs font-medium" style={{ color: streak > 0 ? '#f59e0b' : '#64748b' }}>
              {streak} streak
            </span>
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[10px]" style={{ color: '#64748b' }}>
              Optimal Zone
            </span>
            <div
              className="w-2 h-2 rounded-full"
              style={{ background: currentDifficulty === 'medium' || currentDifficulty === 'hard' ? '#10b981' : '#f59e0b' }}
            />
          </div>
        </div>
      </div>

      {/* Footer */}
      <div
        className="px-4 py-2 flex items-center justify-between"
        style={{
          background: 'rgba(255, 255, 255, 0.02)',
          borderTop: '1px solid rgba(255, 255, 255, 0.05)',
        }}
      >
        <span className="text-[10px]" style={{ color: '#64748b' }}>
          Adapts in real-time
        </span>
        <span className="text-[10px]" style={{ color: '#8b5cf6' }}>
          Zone of Proximal Development
        </span>
      </div>

      {/* Expand overlay */}
      <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300 bg-black/20">
        <div
          className="w-12 h-12 rounded-full flex items-center justify-center"
          style={{
            background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.9) 0%, rgba(59, 130, 246, 0.9) 100%)',
            boxShadow: '0 8px 32px rgba(139, 92, 246, 0.4)',
          }}
        >
          <svg className="w-5 h-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7" />
          </svg>
        </div>
      </div>
    </div>
  );
};

export default AdaptiveLearningPreview;
